import Button from "@ui/button/Button"
import { useState } from "react"
import { toast } from "react-toastify"
import { systemErrorMessage, systemPost } from "src/services/superAdminApi"
import { AdminModal } from "./AdminModal"
import { Callout, Fact, Facts, Fields, Page, Panel, Row } from "./adminUi"

type SpendType = "auth" | "refund" | "Void" | "maintain_fee"

export interface ISandboxCard {
	uid: string
	last4?: string
	currency?: string
}

interface ISimulateSpendResponse {
	issuer_balance_before?: number
	issuer_balance_after?: number
	local_balance_cents?: number
	note?: string
	[key: string]: unknown
}

const SPEND_TYPES: Array<[SpendType, string]> = [["auth", "Authorization"], ["refund", "Refund"], ["Void", "Void"], ["maintain_fee", "Maintenance fee"]]

export const SuperAdminCardSpend = ({ token, card, onClose }: { token: string; card: ISandboxCard; onClose: () => void }) => {
	const [form, setForm] = useState({ amount: "", type: "auth" as SpendType, currency: card.currency || "", origin_serial_number: "" })
	const [loading, setLoading] = useState(false)
	const [result, setResult] = useState<ISimulateSpendResponse | null>(null)
	const needsOrigin = form.type === "refund" || form.type === "Void"

	const submit = async () => {
		const amount = Number(form.amount)
		if (!Number.isInteger(amount) || amount <= 0) return toast.warning("Enter a positive amount in cents")
		if (needsOrigin && !form.origin_serial_number.trim()) return toast.warning("Origin serial number is required for refund and Void")
		setLoading(true)
		setResult(null)
		const response = await systemPost<ISimulateSpendResponse>(token, "system/cards/simulate/spend", {
			card_uid: card.uid,
			amount,
			type: form.type,
			currency: form.currency.trim(),
			origin_serial_number: form.origin_serial_number.trim()
		})
		setLoading(false)
		if (response.error) return toast.error(systemErrorMessage(response.error))
		setResult(response.data)
		toast.success("Sandbox issuer accepted the simulation")
	}

	return <AdminModal title='Simulate issuer spend' label='Simulate sandbox card spend' subtitle={`Sandbox card •••• ${card.last4 || card.uid}`} width={560} closeOnBackdrop onClose={onClose}>
		<Page>
			<Panel>
				<Callout tone='warn' style={{ marginBottom: 16 }}>Amount is entered in cents. The issuer changes its balance immediately; the local balance changes later only through the configured webhook.</Callout>
				<Fields>
					<label>Amount (cents)<input type='number' value={form.amount} placeholder='1000' onChange={(event) => setForm((current) => ({ ...current, amount: event.target.value }))} /></label>
					<label>Type<select name='spend_type' value={form.type} onChange={(event) => setForm((current) => ({ ...current, type: event.target.value as SpendType }))}>{SPEND_TYPES.map(([value, name]) => <option key={value} value={value}>{name}</option>)}</select></label>
					<label>Currency<select value={form.currency || card.currency || "USD"} onChange={(event) => setForm((current) => ({ ...current, currency: event.target.value }))}>{[...new Set([card.currency || "USD", "USD", "EUR"])].map((code) => <option key={code} value={code}>{code}{code === card.currency ? " (card currency)" : ""}</option>)}</select></label>
					<label>{`Origin serial number${needsOrigin ? " (required)" : ""}`}<input value={form.origin_serial_number} placeholder='Required for refund / Void' onChange={(event) => setForm((current) => ({ ...current, origin_serial_number: event.target.value }))} /></label>
				</Fields>
				<Row style={{ marginTop: 16 }}><Button variant='primarySmall' disabled={loading} onClick={() => void submit()}>{loading ? "Sending…" : "Run sandbox simulation"}</Button></Row>
			</Panel>
			{result && <Panel title='Result' hint='What the sandbox issuer reported back.'>
				<div role='status'>
					<Facts>
						<Fact label='Issuer balance'>{result.issuer_balance_before ?? "—"} → {result.issuer_balance_after ?? "—"}</Fact>
						<Fact label='Local balance'>{result.local_balance_cents ?? "—"} cents</Fact>
					</Facts>
					{result.note && <Callout tone='warn' style={{ marginTop: 14 }}>{result.note}</Callout>}
				</div>
			</Panel>}
		</Page>
	</AdminModal>
}
